import { defineStore } from 'pinia'
import { Product, isKubernetesFeatures, isNamespaceFeatures, isPaygFeatures, isClusterFeatures, isAddonFeatures, isVServerFeatures } from '@/types'
import { useNotifyStore } from '@/stores/notifications'
import { uuid } from '@/composables'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError } from '@/composables/useAxiosError'
import { STATUS_CODES } from '@/constants'
import { z } from 'zod'
import { computed, toRaw, ref } from 'vue'
import { safeLocalStorage } from '@/composables/useSSR'

// Domain prices are stored net, but POST /domain grosses them for display at
// the price's own rate. The store holds the stored net, as GET /product and the
// cart pricing read it, so a searched product is normalised on the way in. The
// first key held searched products as they came, gross.
const DOMAIN_PRODUCTS_STORAGE_KEY = 'domain_products_net'
const LEGACY_DOMAIN_PRODUCTS_STORAGE_KEY = 'domain_products'

/** A product from a POST /domain response, with its stored net amounts. */
function fromDomainSearch(product: Product): Product {
  return {
    ...product,
    price: product.price?.map((price) =>
      price.amount && price.tax && price.tax > 0
        ? { ...price, amount: Math.round(price.amount / (1 + price.tax)) }
        : price
    )
  }
}

export const useProductsStore = defineStore('products', () => {
  const products = ref<Product[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  const lastFetched = ref<number | null>(null)

  // Cache duration: 5 minutes
  const CACHE_DURATION = 5 * 60 * 1000

  function readStoredProducts(key: string): Product[] {
    const storage = safeLocalStorage()
    if (!storage) return []

    try {
      const stored = storage.getItem(key)
      if (stored) {
        const parsed = JSON.parse(stored)
        const validated = Product.array().safeParse(parsed)
        if (validated.success) {
          return validated.data
        }
      }
    } catch {
      // Ignore parse errors
    }
    return []
  }

  // Load domain products from localStorage, moving any left under the legacy
  // key over as stored net.
  function loadDomainProducts(): Product[] {
    const current = readStoredProducts(DOMAIN_PRODUCTS_STORAGE_KEY)
    const legacy = readStoredProducts(LEGACY_DOMAIN_PRODUCTS_STORAGE_KEY)
    if (!legacy.length) return current

    const merged = [
      ...current,
      ...legacy.filter((p) => !current.some((c) => c.slug === p.slug)).map(fromDomainSearch)
    ]
    const storage = safeLocalStorage()
    try {
      storage?.setItem(DOMAIN_PRODUCTS_STORAGE_KEY, JSON.stringify(merged))
      storage?.removeItem(LEGACY_DOMAIN_PRODUCTS_STORAGE_KEY)
    } catch {
      // Ignore storage errors
    }
    return merged
  }

  // Save domain products to localStorage
  function saveDomainProducts() {
    const storage = safeLocalStorage()
    if (!storage) return

    try {
      const domainProducts = products.value.filter(
        (p) => p.features && (p.features as Record<string, unknown>).type === 'domain'
      )
      storage.setItem(DOMAIN_PRODUCTS_STORAGE_KEY, JSON.stringify(toRaw(domainProducts)))
    } catch {
      // Ignore storage errors
    }
  }

  // Merge domain products from localStorage into products array
  function mergeDomainProducts() {
    const storedDomainProducts = loadDomainProducts()
    for (const domainProduct of storedDomainProducts) {
      const exists = products.value.some((p) => p.slug === domainProduct.slug)
      if (!exists) {
        products.value.push(domainProduct)
      }
    }
  }

  // GETTERS
  const getProducts = computed((): Product[] => products.value)
  const isLoading = computed((): boolean => loading.value)
  const getError = computed((): string | null => error.value)

  // Helper to check if product has a usable price. The unauthed /product
  // handler eager-loads only `default=true, active=true` prices server-side,
  // so any price present in the wire response is already the default one.
  // We just need a non-zero amount.
  function hasValidPrice(p: Product): boolean {
    return !!(p.price && p.price.length > 0 && p.price.some((price) => !!price.amount))
  }

  // Filter helpers
  const getHostingProducts = computed((): Product[] =>
    products.value.filter(
      (p) =>
        p.public &&
        p.active &&
        p.features &&
        (p.features as Record<string, unknown>).type === 'hosting' &&
        hasValidPrice(p)
    )
  )

  const getDomainProducts = computed((): Product[] =>
    products.value.filter(
      (p) =>
        p.public &&
        p.active &&
        p.features &&
        (p.features as Record<string, unknown>).type === 'domain' &&
        hasValidPrice(p)
    )
  )

  // Kubernetes product getters.
  // Note: we deliberately do NOT re-check p.public/p.active here. The unauthed
  // /product endpoint already filters with WHERE public=true server-side, and
  // these two fields are not exposed in the response shape (ProductDetail
  // strips them). Re-checking them client-side would silently reject every
  // product and fall back to hardcoded display values.
  const getKubernetesProducts = computed((): Product[] =>
    products.value.filter(
      (p) =>
        isKubernetesFeatures(p.features as Record<string, unknown>) &&
        hasValidPrice(p)
    )
  )

  const getKubernetesNamespaceProducts = computed((): Product[] =>
    products.value.filter(
      (p) =>
        isNamespaceFeatures(p.features as Record<string, unknown>) &&
        !isPaygFeatures(p.features as Record<string, unknown>) &&
        hasValidPrice(p)
    )
  )

  const getKubernetesClusterProducts = computed((): Product[] =>
    products.value.filter(
      (p) =>
        isClusterFeatures(p.features as Record<string, unknown>) &&
        hasValidPrice(p)
    )
  )

  const getKubernetesPaygProducts = computed((): Product[] =>
    products.value.filter((p) => isPaygFeatures(p.features as Record<string, unknown>))
  )

  const getKubernetesFreeProducts = computed((): Product[] =>
    products.value.filter((p) => {
      const features = p.features as Record<string, unknown> | undefined
      return features?.free === true
    })
  )

  const getKubernetesAddonProducts = computed((): Product[] =>
    products.value.filter(
      (p) =>
        isAddonFeatures(p.features as Record<string, unknown>) &&
        hasValidPrice(p)
    )
  )

  const getVServerProducts = computed((): Product[] =>
    products.value.filter(
      (p) =>
        p.public &&
        p.active &&
        isVServerFeatures(p.features as Record<string, unknown>) &&
        hasValidPrice(p)
    )
  )

  const getPublicProducts = computed((): Product[] =>
    products.value.filter((p) => p.public && p.active && hasValidPrice(p))
  )

  // Check if cache is still valid
  function isCacheValid(): boolean {
    if (!lastFetched.value) return false
    return Date.now() - lastFetched.value < CACHE_DURATION
  }

  // ACTIONS
  /**
   * Fetch all products from the backend.
   * Uses cache if available and not expired.
   * @param forceRefresh - Skip cache and fetch fresh data
   */
  async function fetchProducts(forceRefresh = false): Promise<Product[]> {
    // Return cached products if valid and not forcing refresh
    if (!forceRefresh && isCacheValid() && products.value.length > 0) {
      return products.value
    }

    loading.value = true
    error.value = null

    try {
      const res = await useAxios<null, Array<Product>>('GET', '/product', true)

      if (!res || res.status !== STATUS_CODES.OK) {
        throw new Error('Produkte konnten nicht geladen werden')
      }

      // Handle null/empty response from API - still merge domain products from localStorage
      if (!res.data || res.data === null) {
        mergeDomainProducts()
        lastFetched.value = Date.now()
        return products.value
      }

      const validate = Product.array().safeParse(res.data)
      if (!validate.success) {
        console.error('Product validation failed:', validate.error)
        throw new Error('Ungültige Produktdaten vom Server')
      }

      products.value = toRaw(validate.data)
      // Merge persisted domain products (from cart additions)
      mergeDomainProducts()
      lastFetched.value = Date.now()
      return products.value
    } catch (err) {
      handleAxiosError(err, { logPrefix: 'ProductsStore:fetchProducts' })
      error.value = err instanceof Error ? err.message : 'Unbekannter Fehler'
      return products.value // Return existing cached products on error
    } finally {
      loading.value = false
    }
  }

  /**
   * Fetch a single product by slug
   */
  async function fetchProductBySlug(slug: string): Promise<Product | null> {
    // First check local cache
    const cached = products.value.find((p) => p.slug === slug)
    if (cached) return cached

    try {
      const res = await useAxios<null, Product>('GET', `/product/${slug}`, true)

      if (!res || res.status !== STATUS_CODES.OK) {
        return null
      }

      const validate = Product.safeParse(res.data)
      if (!validate.success) {
        console.error('Product validation failed:', validate.error)
        return null
      }

      // Add to cache if not already present
      const existingIndex = products.value.findIndex((p) => p.slug === slug)
      if (existingIndex === -1) {
        products.value.push(validate.data)
      } else {
        products.value[existingIndex] = validate.data
      }

      return validate.data
    } catch (err) {
      handleAxiosError(err, { logPrefix: `ProductsStore:fetchProduct:${slug}` })
      return null
    }
  }

  // SETTERS (for admin/direct manipulation)
  function setProducts(payload: Product[]) {
    if (payload.length > 0) {
      const validate = z.array(Product).safeParse(payload)
      if (!validate.success) {
        const notifyStore = useNotifyStore()
        notifyStore.createNotify({
          id: uuid('v4'),
          alertType: 'error',
          title: 'Error',
          message: 'Payload needs to be an array of Products'
        })
        return
      }
      products.value = toRaw(validate.data)
      lastFetched.value = Date.now()
    }
  }

  function addProduct(payload: Product) {
    if (payload) {
      const validate = Product.safeParse(payload)
      if (!validate.success) {
        const notifyStore = useNotifyStore()
        notifyStore.createNotify({
          id: uuid('v4'),
          alertType: 'error',
          title: 'Error',
          message: 'Payload needs to be a Product'
        })
        return
      }

      // Check if product already exists by slug
      if (validate.data.slug) {
        const existingIndex = products.value.findIndex((p) => p.slug === validate.data.slug)
        if (existingIndex !== -1) {
          // Update existing product
          products.value = [
            ...products.value.slice(0, existingIndex),
            validate.data,
            ...products.value.slice(existingIndex + 1)
          ]
          // Persist domain products to localStorage
          saveDomainProducts()
          return
        }
      }

      products.value = [...products.value, validate.data]
      // Persist domain products to localStorage
      saveDomainProducts()
    }
  }

  /** Adds a product from a POST /domain response, as its stored net. */
  function addSearchedDomainProduct(payload: Product) {
    addProduct(fromDomainSearch(payload))
  }

  function updateProduct(payload: Product) {
    if (payload?.slug) {
      const validate = Product.safeParse(payload)
      if (!validate.success) return

      const index = products.value.findIndex((p) => p.slug === payload.slug)
      if (index !== -1) {
        products.value = [
          ...products.value.slice(0, index),
          validate.data,
          ...products.value.slice(index + 1)
        ]
      }
    }
  }

  function removeProduct(slug: string) {
    products.value = products.value.filter((p) => p.slug !== slug)
  }

  function clearCache() {
    products.value = []
    lastFetched.value = null
    error.value = null
    const storage = safeLocalStorage()
    if (storage) {
      storage.removeItem(DOMAIN_PRODUCTS_STORAGE_KEY)
      storage.removeItem(LEGACY_DOMAIN_PRODUCTS_STORAGE_KEY)
    }
  }

  /**
   * Invalidate cache without clearing data.
   * Next fetch will refresh from server.
   */
  function invalidateCache() {
    lastFetched.value = null
  }

  return {
    // State
    products,
    loading,
    error,
    // Getters
    getProducts,
    isLoading,
    getError,
    getHostingProducts,
    getDomainProducts,
    getKubernetesProducts,
    getKubernetesNamespaceProducts,
    getKubernetesClusterProducts,
    getKubernetesPaygProducts,
    getKubernetesFreeProducts,
    getKubernetesAddonProducts,
    getVServerProducts,
    getPublicProducts,
    isCacheValid,
    // Actions
    fetchProducts,
    fetchProductBySlug,
    setProducts,
    addProduct,
    addSearchedDomainProduct,
    updateProduct,
    removeProduct,
    clearCache,
    invalidateCache
  }
})
